// Combo stock — the component items of a Combo, inside AddProductSheet.
//
// Add mode: a server-searched picker (getComboComponentOptions — Regular
// bulk-count stock with something available, first 20 matches) adds rows;
// each row takes a "Qty per combo" and shows how much building the entered
// Combo Quantity will deduct, flagging any shortfall inline.
//
// Edit mode: the components are locked once a combo exists, so the rows are
// read-only (getComboItems, fetched by AddProductSheet) and only preview what
// the Stock Adjustment value will deduct from / give back to each item.
//
// Rows live in the parent's addProductData.comboItems (create) — this
// component only renders them and reports changes, like the rest of the form.
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Trash2 } from "lucide-react";
import { getComboComponentOptionsAction } from "../../Store/actions/apparelAction";
import { FieldRow } from "../../shared/form-section";
import { Combobox } from "ui/combobox";
import { Input } from "ui/input";
import { Button } from "ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "ui/table";
import { cn } from "lib/utils";

const PICKER_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;

export interface ComboItemLine {
  /** Component's productDetails uniqueKey. */
  productDetailId: string;
  productId?: string;
  productName?: string;
  availableCount: number;
  /** Price (MRP) of one piece. */
  mrp?: number | string;
  /** Units of this item per combo — kept as typed (string) until saved. */
  quantity: any;
}

// The sheet is narrow, so the table is fixed-layout with tight cells and
// wrapping text — every column fits without a horizontal scroll.
const TABLE_CLASS = "table-fixed";
const HEAD_CLASS = "h-auto whitespace-normal px-2 py-2 leading-tight";
const CELL_CLASS = "px-2 py-2";

const price = (value: any) => `₹${(Number(value) || 0).toFixed(2)}`;

const lineLabel = (line: any) =>
  [line?.productName, line?.productId].filter(Boolean).join(" ") || "Item";

/** Units `line` needs for `comboQty` combos. */
export function comboLineNeed(line: any, comboQty: number): number {
  return (parseInt(line?.quantity) || 0) * Math.max(comboQty || 0, 0);
}

/** How many units `line` is short by for `comboQty` combos (0 when it fits). */
export function comboLineShortfall(line: any, comboQty: number): number {
  return Math.max(comboLineNeed(line, comboQty) - (Number(line?.availableCount) || 0), 0);
}

/**
 * Add Stock rules for a combo's lines — mirrors
 * ComboStockService.validateComponents on the API, which re-checks them
 * (with locked stock) on save.
 */
export function validateComboLines(lines: ComboItemLine[], comboQty: number): string | undefined {
  if (!lines?.length) return "Add at least one item to the combo.";
  if (lines.some((l) => !/^\d+$/.test(String(l?.quantity ?? "")) || parseInt(l.quantity) < 1)) {
    return "Qty per combo must be a whole number of at least 1.";
  }
  const units = lines.reduce((sum, l) => sum + (parseInt(l.quantity) || 0), 0);
  if (lines.length < 2 && units < 2) {
    return "A combo needs at least 2 items, or 1 item with a quantity of 2 or more.";
  }
  const short = lines.find((l) => comboLineShortfall(l, comboQty) > 0);
  if (short) {
    return `${lineLabel(short)}: need ${comboLineNeed(short, comboQty)}, available ${Number(short.availableCount) || 0}`;
  }
  return undefined;
}

export interface ComboItemsSectionProps {
  isEdit: boolean;
  /** Add: the entered Combo Quantity. Edit: the signed Stock Adjustment value. */
  comboQty: number;
  /** Add mode rows (parent's addProductData.comboItems). */
  items: ComboItemLine[];
  onItemsChange: (items: ComboItemLine[]) => void;
  /** Edit mode rows (getComboItems), read-only. */
  editItems?: any[];
  editLoading?: boolean;
  error?: string;
}

export function ComboItemsSection({
  isEdit,
  comboQty,
  items,
  onItemsChange,
  editItems,
  editLoading,
  error,
}: ComboItemsSectionProps) {
  const dispatch = useDispatch();
  const { getComboComponentOptionsLoad, getComboComponentOptionsData } = useSelector(
    (state: any) => state.ApparelReducerState
  );
  const optionRows: any[] = React.useMemo(
    () => (Array.isArray(getComboComponentOptionsData?.rows) ? getComboComponentOptionsData.rows : []),
    [getComboComponentOptionsData]
  );

  const searchTimer = React.useRef<any>(null);
  const fetchOptions = React.useCallback(
    (searchText: string) =>
      dispatch(getComboComponentOptionsAction({ searchText, limit: PICKER_PAGE_SIZE, offset: 0 })),
    [dispatch]
  );

  // First page on open (Add mode only — Edit never picks new items).
  React.useEffect(() => {
    if (!isEdit) fetchOptions("");
    return () => clearTimeout(searchTimer.current);
  }, [isEdit, fetchOptions]);

  const handleSearchChange = (q: string) => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchOptions(q.trim()), SEARCH_DEBOUNCE_MS);
  };

  const pickedIds = React.useMemo(() => new Set((items || []).map((l) => l.productDetailId)), [items]);
  const pickerOptions = React.useMemo(
    () =>
      optionRows
        .filter((o) => !pickedIds.has(o?.productDetailId))
        .map((o) => ({
          value: String(o?.productDetailId),
          label: `${o?.productId ?? "-"} - ${o?.productName} (Available: ${o?.availableCount}) - ${price(o?.mrp)}`,
        })),
    [optionRows, pickedIds]
  );

  const addLine = (productDetailId: string) => {
    const row = optionRows.find((o) => String(o?.productDetailId) === productDetailId);
    if (!row || pickedIds.has(productDetailId)) return;
    onItemsChange([
      ...(items || []),
      {
        productDetailId: row.productDetailId,
        productId: row.productId,
        productName: row.productName,
        availableCount: Number(row.availableCount) || 0,
        mrp: row.mrp,
        quantity: "1",
      },
    ]);
  };

  const updateQty = (productDetailId: string, value: string) => {
    if (value !== "" && !/^\d+$/.test(value)) return;
    onItemsChange((items || []).map((l) => (l.productDetailId === productDetailId ? { ...l, quantity: value } : l)));
  };

  const removeLine = (productDetailId: string) =>
    onItemsChange((items || []).filter((l) => l.productDetailId !== productDetailId));

  const itemCell = (line: any) => (
    <div className="min-w-0 break-words">
      <div className="font-medium">{line?.productId ?? "-"}</div>
      <div className="text-xs text-muted-foreground">{line?.productName}</div>
      <div className="text-xs text-muted-foreground tabular-nums">{price(line?.mrp)}</div>
    </div>
  );

  if (isEdit) {
    const rows = editItems || [];
    const adjustment = comboQty || 0;
    return (
      <FieldRow
        label="Combo Items"
        full
        error={error}
        hint="Locked — a combo's items can't be changed. A Stock Adjustment below takes from (or gives back to) each item."
      >
        {editLoading && rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">Loading combo items...</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No combo items found.</p>
        ) : (
          <Table className={TABLE_CLASS}>
            <TableHeader>
              <TableRow>
                <TableHead className={HEAD_CLASS}>Item</TableHead>
                <TableHead className={cn(HEAD_CLASS, "w-[84px] text-right")}>Available</TableHead>
                <TableHead className={cn(HEAD_CLASS, "w-[72px] text-right")}>Qty / combo</TableHead>
                <TableHead className={cn(HEAD_CLASS, "w-[92px] text-right")}>Adjustment</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((line: any) => {
                const units = (Number(line?.quantity) || 0) * Math.abs(adjustment);
                const short = adjustment > 0 ? comboLineShortfall(line, adjustment) : 0;
                return (
                  <TableRow key={line?.productDetailId}>
                    <TableCell className={CELL_CLASS}>{itemCell(line)}</TableCell>
                    <TableCell className={cn(CELL_CLASS, "text-right tabular-nums")}>{line?.availableCount ?? 0}</TableCell>
                    <TableCell className={cn(CELL_CLASS, "text-right tabular-nums")}>{line?.quantity}</TableCell>
                    <TableCell className={cn(CELL_CLASS, "text-right tabular-nums")}>
                      {adjustment === 0 ? (
                        <span className="text-muted-foreground">-</span>
                      ) : (
                        <span className={cn(short > 0 && "text-destructive")}>
                          {adjustment > 0 ? `-${units}` : `+${units}`}
                        </span>
                      )}
                      {short > 0 && <div className="text-xs text-destructive">Short by {short}</div>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </FieldRow>
    );
  }

  return (
    <FieldRow
      label="Combo Items"
      required
      full
      error={error}
      hint="Pick the regular stock that makes up one combo. Saving deducts Qty per combo x Combo Quantity from each item."
    >
      <Combobox
        id="comboItemPicker"
        options={pickerOptions}
        value={undefined}
        onValueChange={addLine}
        onSearchChange={handleSearchChange}
        placeholder="Add an item..."
        searchPlaceholder="Search Product ID / Name..."
        emptyText={getComboComponentOptionsLoad ? "Searching..." : "No in-stock items found."}
      />
      {(items || []).length > 0 && (
        <Table className={TABLE_CLASS}>
          <TableHeader>
            <TableRow>
              <TableHead className={HEAD_CLASS}>Item</TableHead>
              <TableHead className={cn(HEAD_CLASS, "w-[84px] text-right")}>Available</TableHead>
              <TableHead className={cn(HEAD_CLASS, "w-[72px] text-center")}>Qty / combo</TableHead>
              <TableHead className={cn(HEAD_CLASS, "w-[84px] text-right")}>Will deduct</TableHead>
              <TableHead className={cn(HEAD_CLASS, "w-[40px]")}><span className="sr-only">Remove</span></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((line) => {
              const need = comboLineNeed(line, comboQty);
              const short = comboLineShortfall(line, comboQty);
              return (
                <TableRow key={line.productDetailId}>
                  <TableCell className={CELL_CLASS}>{itemCell(line)}</TableCell>
                  <TableCell className={cn(CELL_CLASS, "text-right tabular-nums")}>{line.availableCount}</TableCell>
                  <TableCell className={CELL_CLASS}>
                    <Input
                      aria-label={`Qty per combo for ${lineLabel(line)}`}
                      className="mx-auto h-8 w-14 px-1 text-center"
                      inputMode="numeric"
                      value={line.quantity ?? ""}
                      onChange={(e) => updateQty(line.productDetailId, e.target.value)}
                    />
                  </TableCell>
                  <TableCell className={cn(CELL_CLASS, "text-right tabular-nums")}>
                    <span className={cn(short > 0 && "text-destructive")}>{need}</span>
                    {short > 0 && <div className="text-xs text-destructive">Short by {short}</div>}
                  </TableCell>
                  <TableCell className={cn(CELL_CLASS, "px-0")}>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`Remove ${lineLabel(line)}`}
                      onClick={() => removeLine(line.productDetailId)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </FieldRow>
  );
}

export default ComboItemsSection;
